/**
 * The chip — a short status word in a tinted pill.
 *
 * PROMOTED OUT OF `modules/writes/bits.tsx`, whose own header says it was extracted from
 * UsersView in the first place. Three screens deep in one module is where a vocabulary
 * stops being local: the admin pages need the same idiom, and the alternative was a fourth
 * hand-rolled pill with its own alpha values.
 *
 * THE LABEL CARRIES THE MEANING; the colour only reinforces it. That is the rule
 * `StatusBadge.tsx` states — label first, glyph second, colour third — and it is why this
 * component has no variant that renders a bare dot: a status conveyed by hue alone is
 * unreadable to a colour-blind reader and invisible in a printed report, which for a
 * compliance product means it is not evidence.
 *
 * 12% fill / 25% border are the console's tint values. They are stated as `rgb(… / N%)`
 * against the `--*-rgb` triples rather than as separate hex tokens so a chip inherits a
 * palette change instead of pinning last season's green.
 */
import type { ReactNode } from 'react';

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

const TONE_RGB: Record<Tone, string> = {
  ok: 'var(--success-rgb)',
  warn: 'var(--warn-rgb)',
  danger: 'var(--danger-rgb)',
  info: 'var(--info-rgb)',
  muted: 'var(--na-rgb)',
};

const TONE_INK: Record<Tone, string> = {
  ok: 'var(--success-ink)',
  warn: 'var(--warn-ink)',
  danger: 'var(--danger-ink)',
  info: 'var(--info-ink)',
  muted: 'var(--muted-fg)',
};

export function ToneChip({
  tone,
  label,
  title,
  mono,
}: {
  tone: Tone;
  label: string;
  title?: string | undefined;
  /** For identifiers and slugs, where character shape matters more than prose rhythm. */
  mono?: boolean | undefined;
}): ReactNode {
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded-[var(--radius-pill)] px-[8px] py-[1px] text-[11px] font-semibold${
        mono ? ' lm-num' : ''
      }`}
      style={{
        background: `rgb(${TONE_RGB[tone]} / 12%)`,
        border: `1px solid rgb(${TONE_RGB[tone]} / 25%)`,
        color: TONE_INK[tone],
      }}
    >
      {label}
    </span>
  );
}
